export const orderStatuses = ["charged", "prepared", "delivered"];
export const quotationStatuses = ["requested", "pending", "responded", "cancelled"];

export const isQuotationRequest = (item) => item?.rowType === "quotation";

export const getNormalizedStatus = (status, fallback = "pending") =>
  String(status || fallback).toLowerCase();

export const formatStatusLabel = (status) =>
  getNormalizedStatus(status)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const formatOrderStatusLabel = (status) => {
  const normalizedStatus = getNormalizedStatus(status);

  if (normalizedStatus === "charged") return "Charged";
  if (normalizedStatus === "prepared") return "Prepared";

  return formatStatusLabel(normalizedStatus);
};

export const canAddQuoteMedicine = (item) => {
  if (!isQuotationRequest(item)) return false;

  const status = getNormalizedStatus(item.status, "requested");

  return status === "requested" || status === "pending";
};

export const hasQuoteBeenResponded = (item) => {
  const status = getNormalizedStatus(item.status, "requested");

  return ["responded", "closed", "cancelled"].includes(status);
};

export const normalizeQuotationRow = (quotation) => ({
  ...quotation,
  rowId: `quotation-${quotation.id}`,
  rowType: "quotation",
  quotationId: quotation.id,
  source: "quotation",
  status: quotation.status || "requested",
});

export const normalizeOrderRow = (order) => ({
  ...order,
  rowId: `order-${order.id}`,
  rowType: "order",
  quotationId: order.quotationId,
});

export const getUserLabel = (order) => {
  const user = order?.user || order?.customer || order?.client || order?.patient || {};
  const fullName =
    user.name ||
    user.fullName ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    order?.clientName ||
    order?.customerName ||
    order?.userName ||
    order?.patientName ||
    order?.fullName ||
    [order?.firstName, order?.lastName].filter(Boolean).join(" ");

  return (
    fullName ||
    user.email ||
    order?.clientEmail ||
    order?.customerEmail ||
    order?.userEmail ||
    `User #${order?.userId || order?.clientId || order?.customerId || "-"}`
  );
};

export const getAddressLabel = (order) => {
  const address =
    order?.address ||
    order?.deliveryAddress ||
    order?.shippingAddress ||
    order?.user?.address ||
    {};

  const composedAddress = [
    address.addressLine || address.address,
    address.landmark,
    address.city,
    address.state,
    address.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    address.fullAddress ||
    composedAddress ||
    address.street ||
    order?.addressText ||
    order?.deliveryAddressText ||
    "-"
  );
};

export const getOrderTotal = (order) =>
  order?.totalAmount || order?.total || order?.grandTotal || order?.amount || 0;

export const formatOrderDate = (order) => {
  const dateValue = order?.deliveryDate || order?.createdAt || order?.updatedAt;

  if (!dateValue) return "-";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString([], {
    day: "2-digit",
    month: "2-digit",
  });
};

export const formatDateTime = (dateValue) => {
  if (!dateValue) return "-";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString([], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getStatusClass = (status) => {
  const normalizedStatus = getNormalizedStatus(status);

  if (normalizedStatus === "pending" || normalizedStatus === "requested") {
    return "dropdown_requested";
  }
  if (normalizedStatus === "charged" || normalizedStatus === "confirmed") {
    return "dropdown_charged";
  }
  if (normalizedStatus === "prepared" || normalizedStatus === "shipped") {
    return "dropdown_prepared";
  }
  if (normalizedStatus === "responded") {
    return "dropdown_accepted";
  }
  if (normalizedStatus === "shipped") return "dropdown_out_delivery";
  if (normalizedStatus === "delivered") return "dropdown_delivered";
  if (normalizedStatus === "cancelled") return "dropdown_cancelled";

  return "dropdown_order_ready";
};

export const getLineItems = (item) =>
  item?.items ||
  item?.orderItems ||
  item?.products ||
  item?.medicines ||
  item?.quotationItems ||
  item?.quoteItems ||
  [];

export const getItemName = (item) =>
  item?.itemName ||
  item?.medicineName ||
  item?.productName ||
  item?.name ||
  item?.medicine?.name ||
  item?.product?.name ||
  "-";
